/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * WordPress dependencies
 */
import { _x } from '@safe-wordpress/i18n';
import {
	UnitControl,
	type WPUnitControlUnit,
} from '@safe-wordpress/components';

/**
 * Internal dependencies
 */
import {
	getAllValue,
	getAllUnit,
	hasMixedValues,
	hasDefinedValues,
} from './utils';

type AllInputControlProps = {
	readonly className?: string;
	readonly values?:
		| {
				readonly flat?: string;
				readonly topLeft?: string;
				readonly topRight?: string;
				readonly bottomLeft?: string;
				readonly bottomRight?: string;
		  }
		| string;
	readonly min?: number;
	readonly onChange: (
		value?:
			| string
			| {
					readonly flat?: string;
					readonly topLeft?: string;
					readonly topRight?: string;
					readonly bottomLeft?: string;
					readonly bottomRight?: string;
			  }
	) => void;
	readonly selectedUnits: {
		flat?: string;
		topLeft?: string;
		topRight?: string;
		bottomLeft?: string;
		bottomRight?: string;
	};
	readonly setSelectedUnits: ( units: {
		flat?: string;
		topLeft?: string;
		topRight?: string;
		bottomLeft?: string;
		bottomRight?: string;
	} ) => void;
	readonly units?: WPUnitControlUnit[];
	readonly disabled: boolean;
};
export default function AllInputControl( {
	onChange,
	className,
	disabled,
	selectedUnits,
	setSelectedUnits,
	values,
	...props
}: AllInputControlProps ): JSX.Element {
	let allValue = getAllValue( values );

	if ( allValue === undefined ) {
		// If we don't have any value set the unit to any current selection
		// or the most common unit from the individual radii values.
		allValue = getAllUnit( selectedUnits );
	}

	const hasValues = hasDefinedValues( values );
	const isMixed = hasValues && hasMixedValues( values );
	const allPlaceholder = isMixed
		? _x( 'Mixed', 'text', 'nelio-ab-testing' )
		: undefined;

	// Filter out CSS-unit-only values to prevent invalid styles.
	const handleOnChange = ( next?: string ) => {
		const isNumeric = !! next && ! isNaN( parseFloat( next ) );
		const nextValue = isNumeric ? next : undefined;
		onChange( nextValue );
	};

	// Store current unit selection for use as fallback for individual
	// radii controls.
	const handleOnUnitChange = ( unit?: string ) => {
		setSelectedUnits( {
			topLeft: unit,
			topRight: unit,
			bottomLeft: unit,
			bottomRight: unit,
		} );
	};

	return (
		<UnitControl
			{ ...props }
			className={ clsx( className, {
				'nab-components--disabled': disabled,
			} ) }
			aria-label={ _x( 'Border radius', 'text', 'nelio-ab-testing' ) }
			disableUnits={ isMixed }
			disabled={ disabled }
			value={ allValue }
			onChange={ handleOnChange }
			onUnitChange={ handleOnUnitChange }
			placeholder={ allPlaceholder }
			size="__unstable-large"
		/>
	);
}
